/**
 * THE PHOTOS OF ONE VISIT'S REPORT (home #21, `screens/VisitReport.tsx`), built from two artboards in
 * `faithfully-kept-business/design/home/`: the tiles of States-HomeB "10. Your home is ready, on
 * Messages", inside the report's card, and the line under them and the expired card of States-HomeA
 * "4. Photos from a visit". The words are in `words.ts` (`VISIT_REPORT`).
 *
 * - THE TILES (`PhotoTiles`): rows of two square tiles, 8px apart, each with the subtle rule and the
 *   control's radius, the household's own line for it at its bottom left in the eyebrow's ink, as the
 *   artboards draw them.
 *   - HELD: the photo, drawn from home's own address alone (`photoFile`), on the household the page
 *     was read with, cut to its square. Its alt is its line, or its place among them. The line sits
 *     on a small label of the page's cream, so it reads on any photo, and is hidden from a screen
 *     reader, which hears it once, as the alt. Tapped, it opens across both columns at its own shape,
 *     and tapped again closes: a button that says whether it is open, never a move to anywhere.
 *   - COMING: the artboards' hatched tile, with its line, while home's server reads it.
 *   - FAILED, as the page read it or as the browser drew it: the hatched tile, with its line and
 *     "Did not load".
 * - THE LINES UNDER THEM, in a region a screen reader hears change: "The photos are on their way."
 *   while one is coming; where one did not load, that some did not load, to open the page again in a
 *   few minutes, with the phone; and once every one is held, artboard 4's "These photos are for you
 *   only." with the day they may be seen here until, in place of its "The links last 30 days.",
 *   which is not true here.
 * - THE EXPIRED CARD (`PhotosAgain`), as artboard 4 draws it, white with the subtle rule, the card's
 *   radius, 20px inside and 10px between its parts: "The photos from Tuesday 13 October have
 *   expired." and "Ask for them again", the artboard's secondary button, white with a navy rule and
 *   56 tall. Where every photo is no longer on her page instead (the Worker's `photosGone`), the same
 *   card says "The photos from Tuesday 13 October are not on your page any more.", so a photo ops
 *   took back is said rather than gone quietly until its link runs out. The press sends
 *   `journal.photos` for the visit through the request door, under one key minted for the card, so a
 *   press again after a failure names the same request.
 *   - ONCE THE DOOR HAS IT, and where the Worker holds the photos again as the page is read because an
 *     ask for them already waits, by the door's own hold (the visit's `held`), the button gives way
 *     to "Sent to the office.", as Feedback says it, and never that the photos are coming back, which
 *     is the office's to say (CLAUDE.md binding rule 1). The line takes the focus the button had.
 *   - EVERY REFUSAL IS SAID IN PLACE, with the phone, never a silent press (the owner, 2026-10-20):
 *     one already waiting (`409 already_asked`) and the photos changed since the page was read
 *     (`409 not_expired`) in place of the button, which would be refused again; the door past its
 *     limits (`429`) and anything that did not go through under the button, which may go later. A
 *     401 is the door; a 404 reads the page again, as Feedback does.
 *
 * In the website's tokens alone, never a raw colour: the hatch is the sunken surface and the page's
 * cream, close to the artboards' two creams.
 */
import { useEffect, useRef, useState } from 'react';
import { PHOTOS_AGAIN, photoFile, sendRequest, type PhotoState, type TooMany } from '../api.ts';
import { FAILED_BEFORE_PHONE, REQUEST, VISIT_REPORT, tooManyBeforePhone } from '../words.ts';
import { Phone } from './Phone.tsx';

/** A photo as the page draws it: where it stands as last read, or failed as the browser drew it. */
export interface PhotoDrawn {
  readonly id: string;
  readonly caption?: string;
  readonly state: PhotoState;
}

/** The artboards' tile: square, the subtle rule, the control's radius. */
const TILE = 'rounded-control border border-border-subtle';

/** The artboards' hatched tile, in the website's tokens, its line at the bottom left. */
const HATCHED =
  `${TILE} flex aspect-square flex-col items-start justify-end p-1.5 ` +
  'bg-[image:repeating-linear-gradient(45deg,var(--color-surface-sunken)_0_8px,var(--color-surface-page)_8px_16px)] ' +
  'text-left text-[0.8125rem] leading-snug text-text-emphasis';

/** The line on a held photo: the artboards' 13px in the eyebrow's ink, on a small cream label. */
const LABEL =
  'absolute bottom-1.5 left-1.5 max-w-[calc(100%-0.75rem)] rounded-sm bg-surface-page px-1.5 ' +
  'text-left text-[0.8125rem] leading-snug text-text-emphasis';

/** The lines under the photos, as artboard 4 draws "These photos are for you only.". */
const LINE = 'text-small text-pretty text-text-muted';

interface PhotoTilesProps {
  /** Ops's id of the visit, which home's own address for each photo names. */
  readonly visitId: string;
  /** The household the visit's page was read with, which each address names too. */
  readonly household: string;
  readonly photos: readonly PhotoDrawn[];
  /** The day they may be seen here until, in words, or null where no photo says. */
  readonly until: string | null;
  /** Says a held photo's image did not load in the browser. */
  readonly onBroken: (photoId: string) => void;
}

export function PhotoTiles({ visitId, household, photos, until, onBroken }: PhotoTilesProps) {
  const [open, setOpen] = useState<string | null>(null);
  const coming = photos.some((photo) => photo.state === 'coming');
  const failed = photos.some((photo) => photo.state === 'failed');
  return (
    <div className="flex flex-col gap-2.5">
      <ul className="grid grid-cols-2 gap-2">
        {photos.map((photo, index) => {
          const opened = open === photo.id && photo.state === 'held';
          return (
            <li key={photo.id} className={opened ? 'col-span-2' : undefined}>
              {photo.state === 'held' ? (
                <button
                  type="button"
                  aria-expanded={opened}
                  onClick={() => setOpen(opened ? null : photo.id)}
                  className={`${TILE} relative block w-full overflow-hidden bg-surface-sunken ${
                    opened ? '' : 'aspect-square'
                  }`}
                >
                  <img
                    src={photoFile(visitId, photo.id, household)}
                    alt={photo.caption ?? VISIT_REPORT.photoOf(index + 1, photos.length)}
                    onError={() => onBroken(photo.id)}
                    className={
                      opened ? 'block h-auto w-full object-contain' : 'block size-full object-cover'
                    }
                  />
                  {photo.caption === undefined ? null : (
                    <span aria-hidden="true" className={LABEL}>
                      {photo.caption}
                    </span>
                  )}
                </button>
              ) : (
                <div className={HATCHED}>
                  {photo.caption === undefined ? null : <span>{photo.caption}</span>}{' '}
                  {photo.state === 'failed' ? <span>{VISIT_REPORT.didNotLoad}</span> : null}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div aria-live="polite" className="flex flex-col gap-2.5">
        {coming ? <p className={LINE}>{VISIT_REPORT.onTheirWay}</p> : null}
        {failed ? (
          <p className={LINE}>
            {VISIT_REPORT.notLoadedBeforePhone} <Phone after="." />
          </p>
        ) : null}
        {coming || failed ? null : (
          <p className={LINE}>
            {until === null ? VISIT_REPORT.forYou : VISIT_REPORT.forYouUntil(until)}
          </p>
        )}
      </div>
    </div>
  );
}

/** What the card says of an ask, under or in place of its button. */
type Said =
  | { readonly kind: 'sent' }
  | { readonly kind: 'already' }
  | { readonly kind: 'changed' }
  | { readonly kind: 'too_many'; readonly limit?: TooMany }
  | { readonly kind: 'failed' };

/** The answers after which asking again would be refused again, so the button gives way. */
const GIVES_WAY: ReadonlySet<Said['kind']> = new Set(['sent', 'already', 'changed']);

/** Artboard 4's secondary button: white, a navy rule 2px wide, at least 56 tall. */
const AGAIN_BUTTON =
  'flex min-h-tap-call w-full items-center justify-center rounded-control border-2 ' +
  'border-surface-brand bg-surface-raised px-6 text-body font-semibold text-text-brand ' +
  'hover:bg-surface-accent-soft';

interface PhotosAgainProps {
  readonly visitId: string;
  readonly household: string;
  /** The visit's day in words, or null for a visit with none. */
  readonly day: string | null;
  /**
   * Why she may ask for them again: a link has run out (`expired`), or every photo is no longer on
   * her page (`gone`).
   */
  readonly why: 'expired' | 'gone';
  /** The Worker holds them again, as the page was last read: an ask for them already waits. */
  readonly asked: boolean;
  /** Asks /api/me again, after the door says the session has ended. */
  readonly onSignedOut: () => void;
  /** Reads the page again, after the door does not find the visit for her. */
  readonly reload: () => void;
}

export function PhotosAgain({
  visitId,
  household,
  day,
  why,
  asked,
  onSignedOut,
  reload,
}: PhotosAgainProps) {
  const [pressedSaid, setSaid] = useState<Said | null>(null);
  // What a press here said, or else, where the Worker holds them, that one is with the office.
  const said: Said | null = pressedSaid ?? (asked ? { kind: 'sent' } : null);
  const [pressed, setPressed] = useState(0);
  const key = useRef<string | null>(null);
  const out = useRef(false);
  const mounted = useRef(true);
  const line = useRef<HTMLParagraphElement>(null);
  const givenWay = said !== null && GIVES_WAY.has(said.kind);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The line takes the focus where the button it stood under gave way to it.
  useEffect(() => {
    if (pressed > 0 && givenWay) line.current?.focus();
  }, [pressed, givenWay]);

  async function ask() {
    if (out.current) return;
    out.current = true;
    key.current ??= crypto.randomUUID();
    const answer = await sendRequest({
      key: key.current,
      type: PHOTOS_AGAIN,
      household,
      payload: { visit_id: visitId },
    });
    out.current = false;
    if (!mounted.current) return;
    switch (answer.outcome) {
      case 'sent':
        setSaid({ kind: 'sent' });
        break;
      case 'signed_out':
        onSignedOut();
        return;
      case 'already_asked':
        setSaid({ kind: 'already' });
        break;
      case 'too_many':
        setSaid(
          answer.limit === undefined
            ? { kind: 'too_many' }
            : { kind: 'too_many', limit: answer.limit },
        );
        break;
      case 'refused':
        setSaid(answer.code === 'not_expired' ? { kind: 'changed' } : { kind: 'failed' });
        break;
      case 'not_found':
        setSaid({ kind: 'failed' });
        reload();
        break;
      default:
        setSaid({ kind: 'failed' });
    }
    setPressed((count) => count + 1);
  }

  return (
    <div className="flex flex-col rounded-card border border-border-subtle bg-surface-raised p-5">
      <p className="text-small text-pretty text-text-body">{cardLine(why, day)}</p>
      {givenWay ? null : (
        <button type="button" onClick={() => void ask()} className={`mt-2.5 ${AGAIN_BUTTON}`}>
          {VISIT_REPORT.askAgain}
        </button>
      )}
      <div aria-live="polite">
        {said === null ? null : (
          <p ref={line} tabIndex={-1} className="mt-2.5 text-small text-pretty text-text-body">
            <SaidWords said={said} />
          </p>
        )}
      </div>
    </div>
  );
}

/** The card's own line: why the photos may be asked for again, with the visit's day where it has one. */
function cardLine(why: 'expired' | 'gone', day: string | null): string {
  if (why === 'gone') return day === null ? VISIT_REPORT.gone : VISIT_REPORT.goneFrom(day);
  return day === null ? VISIT_REPORT.expired : VISIT_REPORT.expiredFrom(day);
}

/** The words of what the card says, with the phone after each that ends in "call". */
function SaidWords({ said }: { readonly said: Said }) {
  switch (said.kind) {
    case 'sent':
      return <>{REQUEST.sent}</>;
    case 'already':
      return (
        <>
          {VISIT_REPORT.askedAlreadyBeforePhone} <Phone after="." />
        </>
      );
    case 'changed':
      return (
        <>
          {VISIT_REPORT.changedBeforePhone} <Phone after="." />
        </>
      );
    case 'too_many':
      return (
        <>
          {tooManyBeforePhone(said.limit)} <Phone after="." />
        </>
      );
    case 'failed':
      return (
        <>
          {FAILED_BEFORE_PHONE} <Phone after="." />
        </>
      );
  }
}
